import {
 AxiosResponse
} from 'axios';
import {
 bexoHttpClient
} from '../../clients';

/**
 * Service class for performing schema operations.
 */
class SchemaService {

    /**
     * Fetches the schemas from the server.
     * @param {string} tableName - The name of the table to retrieve the schema for.
     * @param {string} appId - The ID of the app to retrieve the schema for.
     * @param {string} oragID - The ID of the tenant to retrieve the schema for.
     * @param {string | null} accessToken - The access token for authorization.
     * @returns {Promise<AxiosResponse>} The response from the server containing the schemas.
     */
    async retrieveSchemas(
        tableName = '*',
        appId: string,
        oragID: string,
        accessToken: string | null = null
    ): Promise<AxiosResponse> {

        const response = await bexoHttpClient.get('/retrieve/schemas/V2', {
            params: {
                tenant_id: oragID,
                app_id: appId,
                TableName: tableName,
            },
            headers: {
                'access-token': accessToken,
            },
        });
        return response;
    }

    /**
     * Creates multiple schemas.
     * @param {string} oragID - The ID of the tenant to create schemas for.
     * @param {string} appId - The ID of the app to create schemas for.
     * @param {object} payload - The payload containing schema data.
     * @returns {Promise<string>} The response message from the server after schema creation.
     */
    async createSchemas(
        oragID: string,
        appId: string,
        payload: object
    ): Promise<string> {

        try {
            const response = await bexoHttpClient.post('/create/schemas/V2', payload, {
                params: {
                    tenant_id: oragID,
                    app_id: appId,
                },
                headers: {
                    'Content-Type': 'application/json',
                },
            });

             // Axios stores response data in response.data
            return response.data.message; // Extract and return only the message
        } catch (error: any) {
            console.error('Failed to create schemas:', error.response ? error.response.data : error.message);
            throw new Error(`Failed to create schemas: ${JSON.stringify(error.response ? error.response.data : error.message)}`);
        }
    }

    /**
     * Modifies existing schemas.
     * @param {object} payload - The modified schemas data object.
     * @param {string} appId - The ID of the app for which the schemas are being modified.
     * @param {string} oragID - The ID of the tenant for which the schemas are being modified.
     * @param {string | null} accessToken - The access token for authorization.
     * @returns {Promise<object>} The response from the server after modifying the schemas.
     */
    async modifySchemas(
        payload: object,
        appId: string,
        oragID: string,
        accessToken: string | null = null
    ): Promise<object> {
        try {
            const response = await bexoHttpClient.put('/modify/schemas/V2', payload, {
                params: {
                    tenant_id: oragID,
                    app_id: appId,
                },
                headers: {
                    'Content-Type': 'application/json',
                    'access-token': accessToken, // Correctly adding the token in the headers
                },
            });

            return response.data; // Ensure the response is returned correctly
        } catch (error: any) {
            console.error('Failed to modify schemas:', error.response ? error.response.data : error.message);
            throw new Error(`Failed to modify schemas: ${JSON.stringify(error.response ? error.response.data : error.message)}`);
        }
    }

    /**
     * Deletes multiple schemas.
     * @param {object} payload - The payload containing schema IDs to delete.
     * @param {string} oragID - The ID of the tenant to delete schemas for.
     * @param {string} appId - The ID of the app to delete schemas for.
     * @returns {Promise<AxiosResponse>} The response from the server after deleting the schemas.
     */
    async deleteSchemas(
        payload: object,
        oragID: string,
        appId: string
    ): Promise<AxiosResponse> {

        const response = await bexoHttpClient.delete('/delete/multiple_schemas/V2', {
            params: {
                tenant_id: oragID,
                app_id: appId,
            },
            data: payload,
            headers: {
                'Content-Type': 'application/json',
            },
        });

        return response;
    }
}

const schemaService = new SchemaService();
export { schemaService };
